import type { PermissionCheck } from './schema'

import { hasPermission } from './scope'

export type PermissionMode = 'all' | 'any'

/**
 * The hook {@link createUseHasPermissions} builds. Feature packages take it
 * from the host app through a provider. Returns `undefined` while grants load.
 */
export type UseHasPermissions = (
  requiredPermissions: PermissionCheck[],
  mode?: PermissionMode,
) => boolean | undefined

/**
 * Minimal shape the factory needs from a "current user" query hook: the grants,
 * plus loading/error flags. web-ui satisfies this with RTK's `useGetSelfQuery`.
 */
export interface SelfPermissionsResult {
  data?: { permissions?: readonly string[] } | null
  isLoading: boolean
  error?: unknown
}

/**
 * Builds a `useHasPermissions` hook bound to a caller-supplied "self" query
 * hook. Keeping the data source injected lets this package stay free of the
 * app's store/RTK — the consuming app binds its own `useSelf` (see web-ui's
 * `@/hooks/use-has-permissions`).
 *
 * The returned hook is scope-aware (see `./scope`) and returns `true`/`false`,
 * or `undefined` while the grants are still loading.
 *
 * @param useSelf Hook returning the current user's permission grants + status
 * @returns A `useHasPermissions(requiredPermissions, mode)` hook
 */
export function createUseHasPermissions(
  useSelf: () => SelfPermissionsResult,
): UseHasPermissions {
  return function useHasPermissions(
    requiredPermissions: PermissionCheck[],
    mode: PermissionMode = 'all',
  ): boolean | undefined {
    const { data, isLoading, error } = useSelf()

    if (isLoading) {
      return
    }

    const grants = data?.permissions
    if (error || !grants) {
      return false
    }

    const check = (req: PermissionCheck) => hasPermission(grants, req)

    return mode === 'any'
      ? requiredPermissions.some(check)
      : requiredPermissions.every(check)
  }
}
